<template>
  <div v-if="show" class="qrcode-modal-overlay" @click.self="close">
    <div class="qrcode-modal">
      <div class="qrcode-modal-header">
        <h3 class="qrcode-modal-title">
          <i class="fas fa-qrcode mr-2"></i>
          {{ title }}
        </h3>
        <button class="qrcode-modal-close" @click="close" aria-label="Fechar">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="qrcode-modal-body">
        <div v-if="loading" class="qrcode-loading">
          <i class="fas fa-spinner fa-spin"></i>
          <p>Gerando QR Code...</p>
        </div>
        <div v-else-if="error" class="qrcode-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>
        <div v-else-if="qrCodeDataUrl" class="qrcode-content">
          <img :src="qrCodeDataUrl" alt="QR Code" class="qrcode-image" />
          <div class="qrcode-url-container">
            <p class="qrcode-url" :title="url">{{ shortenedUrl }}</p>
            <button class="qrcode-copy-btn" @click="copyUrl" :title="copied ? 'Copiado!' : 'Copiar URL'">
              <i :class="copied ? 'fas fa-check' : 'fas fa-copy'"></i>
            </button>
          </div>
          <p class="qrcode-hint">Escaneie com seu celular para acessar</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import { rootCssVar } from '../../utils/rootCssVar';

export default {
  name: 'QRCodeModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'Acesso Rápido'
    }
  },
  data() {
    return {
      qrCodeDataUrl: null,
      loading: false,
      error: null,
      copied: false
    };
  },
  computed: {
    shortenedUrl() {
      if (!this.url) return '';
      const maxLength = 50;
      if (this.url.length <= maxLength) {
        return this.url;
      }
      // Encurtar mantendo início e fim
      const start = this.url.substring(0, 25);
      const end = this.url.substring(this.url.length - 20);
      return `${start}...${end}`;
    }
  },
  watch: {
    show(newVal) {
      if (newVal && this.url) {
        this.generateQRCode();
      } else {
        this.reset();
      }
    },
    url() {
      if (this.show && this.url) {
        this.generateQRCode();
      }
    }
  },
  methods: {
    async generateQRCode() {
      if (!this.url) {
        this.error = 'URL não fornecida';
        return;
      }

      this.loading = true;
      this.error = null;
      this.qrCodeDataUrl = null;

      try {
        const dark = rootCssVar('--qr-modules-dark');
        const light = rootCssVar('--qr-modules-light');
        const dataUrl = await QRCode.toDataURL(this.url, {
          width: 300,
          margin: 2,
          color: {
            dark,
            light
          }
        });
        this.qrCodeDataUrl = dataUrl;
      } catch (err) {
        console.error('Erro ao gerar QR Code:', err);
        this.error = 'Erro ao gerar QR Code. Tente novamente.';
      } finally {
        this.loading = false;
      }
    },
    reset() {
      this.qrCodeDataUrl = null;
      this.loading = false;
      this.error = null;
    },
    close() {
      this.$emit('close');
    },
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.url);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Erro ao copiar URL:', err);
        // Fallback para navegadores antigos
        const textArea = document.createElement('textarea');
        textArea.value = this.url;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.copied = true;
          setTimeout(() => {
            this.copied = false;
          }, 2000);
        } catch (fallbackErr) {
          console.error('Erro ao copiar URL (fallback):', fallbackErr);
        }
        document.body.removeChild(textArea);
      }
    }
  }
};
</script>

<style scoped>
.qrcode-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s;
}

.qrcode-modal {
  background: var(--table-bg);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s;
}

.qrcode-modal-header {
  padding: 1.5em;
  border-bottom: 1px solid var(--qs-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.qrcode-modal-title {
  margin: 0;
  color: var(--table-fg);
  font-size: 1.2em;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.qrcode-modal-close {
  background: none;
  border: none;
  color: var(--table-fg);
  font-size: 1.5em;
  cursor: pointer;
  padding: 0.25em;
  line-height: 1;
  transition: color 0.2s;
}

.qrcode-modal-close:hover {
  color: var(--lp-validation-error);
}

.qrcode-modal-body {
  padding: 2em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.qrcode-loading,
.qrcode-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: var(--table-fg);
}

.qrcode-loading i,
.qrcode-error i {
  font-size: 2.5em;
  margin-bottom: 1em;
  color: var(--primary);
}

.qrcode-error i {
  color: var(--lp-validation-error);
}

.qrcode-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.qrcode-image {
  width: 100%;
  max-width: 300px;
  height: auto;
  border-radius: 8px;
  background: var(--table-surface);
  padding: 1em;
  margin-bottom: 1.5em;
}

.qrcode-url-container {
  display: flex;
  align-items: center;
  gap: 0.5em;
  width: 100%;
  margin-bottom: 0.5em;
  padding: 0.75em;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
}

.qrcode-url {
  flex: 1;
  word-break: break-all;
  color: var(--table-fg);
  font-size: 0.85em;
  text-align: left;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.qrcode-copy-btn {
  background: rgba(95, 37, 159, 0.3);
  border: 1px solid rgba(95, 37, 159, 0.5);
  border-radius: 4px;
  color: var(--table-fg);
  cursor: pointer;
  padding: 0.5em 0.75em;
  font-size: 0.9em;
  transition: all 0.2s;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
}

.qrcode-copy-btn:hover {
  background: rgba(95, 37, 159, 0.5);
  border-color: var(--primary);
  transform: scale(1.05);
}

.qrcode-copy-btn:active {
  transform: scale(0.95);
}

.qrcode-copy-btn i.fa-check {
  color: var(--qs-success-bright);
}

.qrcode-hint {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85em;
  margin: 0;
  text-align: center;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>

